import { useState, useRef, useEffect } from 'react';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonButtons,
  IonButton,
  IonMenuButton,
  IonTitle,
  IonContent,
  IonIcon,
  IonDatetime,
  IonText,
  IonSlides,
  IonSlide,
  IonRippleEffect,
  IonRefresher,
  IonModal,
  IonRefresherContent,
  useIonViewWillEnter,
  useIonViewWillLeave
} from '@ionic/react';
import {
  calendarOutline,
  chevronDownOutline,
  chevronForwardOutline,
  downloadOutline
} from 'ionicons/icons';
import { observer } from 'mobx-react-lite';
import moment from 'moment';
import { getPrivateFile } from 'libs/helpers/helper.lib';

import ListLoader from './components/ListLoader/ListLoader';
import FuelingInfoContainer from './components/FuelingInfoCotainer';
import SanatizedHtml from 'components/SanatizedHtml';
import TwoOFour from 'components/TwoOFour';

import apolloClient from 'libs/apollo/apollo.lib';

import fuelingPlanStore from 'stores/fueling-plan.store';

import { sliderConfig } from 'config.json';
import classes from './style.module.scss';

const FUELING_PLAN_FIELDS = `
  id
  start_date
  end_date
  name
  description
  cover
  fueling_plan_doc
  calories_intake
  protein_intake
  carbohydrate_intake
  fat_intake
  goal_of_the_week
  recommended_supplements
  nutrition_action_plan_goal
  other_nutrition
  recommendations
  notes
  fuelingPlanCategory {
    id
    name
  }
`;

const FuelingPlanPage = ({ match, history }) => {
  const [date, setDate] = useState();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const datePicker = useRef();
  const [showDietDescription, setShowDietDescription] = useState(false);
  const [showGoalOfTheWeek, setShowGoalOfTheWeek] = useState(false);
  const [showRecomSupplement, setShowRecomSupplement] = useState(false);
  const [showNutritionActionPlanGoal, setShowNutritionActionPlanGoal] =
    useState(false);
  const [showOtherNutrition, setShowOtherNutrition] = useState(false);
  const [showRecommendations, setShowRecommendations] = useState(false);
  const fuelingPlans = fuelingPlanStore.getObjects();

  useIonViewWillEnter(() => {
    setDate(moment().format('YYYY-MM-DD'));
  });

  useIonViewWillLeave(() => {
    fuelingPlanStore.resetAllFields();
    setDate(null);
  });

  useEffect(() => {
    if (date) {
      const searchDate = date;
      const query = {
        AND: [
          {
            column: 'start_date',
            operator: 'LTE',
            value: searchDate
          },
          {
            column: 'end_date',
            operator: 'GTE',
            value: searchDate
          }
        ]
      };
      fuelingPlanStore.setFields(FUELING_PLAN_FIELDS);
      fuelingPlanStore.setQuery(query);
      fetchFuelingPlans();
    }

    return () => {
      if (date) {
        fuelingPlanStore.setObjects([]);
      }
    };
  }, [date]);

  const fetchFuelingPlans = async () => {
    try {
      await fuelingPlanStore.searchObjects();
    } catch (error) {
      console.log(error);
    }
  };

  const _renderLoader = () => {
    return <ListLoader />;
  };

  const _renderNoContent = () => {
    return <TwoOFour message='No fueling plan assigned!' />;
  };

  const _renderContent = () => {
    const isLoading = fuelingPlanStore.getIsLoading();
    const noContent = fuelingPlans.length === 0;

    if (isLoading && !isRefreshing) {
      return _renderLoader();
    }

    if (noContent) {
      return _renderNoContent();
    }

    return (
      <IonSlides
        options={sliderConfig}
        className={classes.ionSlides}
        onIonSlidesDidLoad={async (e) => {
          const swiperLocal = await e.currentTarget.getSwiper();
          const query = new URLSearchParams(history.location.search);
          if (query.has('slide')) {
            const slideToIndex = query.get('slide');
            swiperLocal.slideTo(slideToIndex);
          }
        }}
      >
        {fuelingPlans.map((fuelingPlan, index) => (
          <IonSlide key={fuelingPlan.id} className={classes.ionSlide}>
            <div className={classes.fuelingPlanInfoContainer}>
              <FuelingInfoContainer fuelingPlan={fuelingPlan} />
            </div>
            <div className={classes.fuelingPlanStatesDetails}>
              <div className={classes.fuelingPlanStatesDetails__states}>
                <div className={classes.fuelingPlanStatesDetails__statesRows}>
                  <IonText className='bold'> Calories Intake</IonText>
                  <IonText className='bold'>
                    {fuelingPlan.calories_intake ?? '--'}
                  </IonText>
                </div>
                <div className={classes.fuelingPlanStatesDetails__statesRows}>
                  <IonText className='bold'> Protein Intake</IonText>
                  <IonText className='bold'>
                    {fuelingPlan.protein_intake
                      ? `${fuelingPlan.protein_intake}g`
                      : '--'}
                  </IonText>
                </div>
                <div className={classes.fuelingPlanStatesDetails__statesRows}>
                  <IonText className='bold'> Fat Intake</IonText>
                  <IonText className='bold'>
                    {fuelingPlan.fat_intake
                      ? `${fuelingPlan.fat_intake}g`
                      : '--'}
                  </IonText>
                </div>
                <div className={classes.fuelingPlanStatesDetails__statesRows}>
                  <IonText className='bold'> Carbohydrates </IonText>
                  <IonText className='bold'>
                    {fuelingPlan.carbohydrate_intake
                      ? `${fuelingPlan.carbohydrate_intake}g`
                      : '--'}
                  </IonText>
                </div>
              </div>
              <div className={classes.fuelingPlanStatesDetails__details}>
                <div className={classes.fuelingPlanStatesDetails__detailBlocks}>
                  <div
                    className={`${classes.fuelingPlanDecButton} ion-activatable ripple-parent`}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (parseInt(showDietDescription) === index) {
                        setShowDietDescription(null);
                      } else {
                        setShowDietDescription(index);
                      }
                    }}
                  >
                    <IonText color='light' className='big bold'>
                      Diet Description
                    </IonText>
                    <IonIcon
                      color='light'
                      icon={
                        showDietDescription === index
                          ? chevronDownOutline
                          : chevronForwardOutline
                      }
                    />
                    <IonRippleEffect></IonRippleEffect>
                  </div>
                  {showDietDescription === index && (
                    <div className={classes.fuelingPlanDecDetails}>
                      <SanatizedHtml dirtyHtml={fuelingPlan.description} />
                    </div>
                  )}
                  <div className={classes.fuelPlanDecButtonDivider}></div>
                </div>
                <div className={classes.fuelingPlanStatesDetails__detailBlocks}>
                  <div
                    className={`${classes.fuelingPlanDecButton} ion-activatable ripple-parent`}
                    onClick={() => {
                      if (parseInt(showGoalOfTheWeek) === index) {
                        setShowGoalOfTheWeek(null);
                      } else {
                        setShowGoalOfTheWeek(index);
                      }
                    }}
                  >
                    <IonText color='light' className='big bold'>
                      Goal of the week
                    </IonText>
                    <IonIcon
                      color='light'
                      icon={
                        showGoalOfTheWeek === index
                          ? chevronDownOutline
                          : chevronForwardOutline
                      }
                    />
                    <IonRippleEffect></IonRippleEffect>
                  </div>
                  {showGoalOfTheWeek === index && (
                    <div className={classes.fuelingPlanDecDetails}>
                      <SanatizedHtml dirtyHtml={fuelingPlan.goal_of_the_week} />
                    </div>
                  )}
                  <div className={classes.fuelPlanDecButtonDivider}></div>
                </div>
                <div className={classes.fuelingPlanStatesDetails__detailBlocks}>
                  <div
                    className={`${classes.fuelingPlanDecButton} ion-activatable ripple-parent`}
                    onClick={() => {
                      if (parseInt(showRecomSupplement) === index) {
                        setShowRecomSupplement(null);
                      } else {
                        setShowRecomSupplement(index);
                      }
                    }}
                  >
                    <IonText color='light' className='big bold'>
                      Recommended Supplements
                    </IonText>
                    <IonIcon
                      color='light'
                      icon={
                        showRecomSupplement === index
                          ? chevronDownOutline
                          : chevronForwardOutline
                      }
                    />
                    <IonRippleEffect></IonRippleEffect>
                  </div>
                  {showRecomSupplement === index && (
                    <div className={classes.fuelingPlanDecDetails}>
                      <SanatizedHtml
                        dirtyHtml={fuelingPlan.recommended_supplements}
                      />
                    </div>
                  )}
                  <div className={classes.fuelPlanDecButtonDivider}></div>
                </div>
                <div className={classes.fuelingPlanStatesDetails__detailBlocks}>
                  <div
                    className={`${classes.fuelingPlanDecButton} ion-activatable ripple-parent`}
                    onClick={() => {
                      if (parseInt(showNutritionActionPlanGoal) === index) {
                        setShowNutritionActionPlanGoal(null);
                      } else {
                        setShowNutritionActionPlanGoal(index);
                      }
                    }}
                  >
                    <IonText color='light' className='big bold'>
                      Nutrition Action Plan Goal
                    </IonText>
                    <IonIcon
                      color='light'
                      icon={
                        showNutritionActionPlanGoal === index
                          ? chevronDownOutline
                          : chevronForwardOutline
                      }
                    />
                    <IonRippleEffect></IonRippleEffect>
                  </div>
                  {showNutritionActionPlanGoal === index && (
                    <div className={classes.fuelingPlanDecDetails}>
                      <SanatizedHtml
                        dirtyHtml={fuelingPlan.nutrition_action_plan_goal}
                      />
                    </div>
                  )}
                  <div className={classes.fuelPlanDecButtonDivider}></div>
                </div>
                <div className={classes.fuelingPlanStatesDetails__detailBlocks}>
                  <div
                    className={`${classes.fuelingPlanDecButton} ion-activatable ripple-parent`}
                    onClick={() => {
                      if (parseInt(showOtherNutrition) === index) {
                        setShowOtherNutrition(null);
                      } else {
                        setShowOtherNutrition(index);
                      }
                    }}
                  >
                    <IonText color='light' className='big bold'>
                      Other Nutrition
                    </IonText>
                    <IonIcon
                      color='light'
                      icon={
                        showOtherNutrition === index
                          ? chevronDownOutline
                          : chevronForwardOutline
                      }
                    />
                    <IonRippleEffect></IonRippleEffect>
                  </div>
                  {showOtherNutrition === index && (
                    <div className={classes.fuelingPlanDecDetails}>
                      <SanatizedHtml dirtyHtml={fuelingPlan.other_nutrition} />
                    </div>
                  )}
                  <div className={classes.fuelPlanDecButtonDivider}></div>
                </div>
                <div className={classes.fuelingPlanStatesDetails__detailBlocks}>
                  <div
                    className={`${classes.fuelingPlanDecButton} ion-activatable ripple-parent`}
                    onClick={() => {
                      if (parseInt(showRecommendations) === index) {
                        setShowRecommendations(null);
                      } else {
                        setShowRecommendations(index);
                      }
                    }}
                  >
                    <IonText color='light' className='big bold'>
                      Recommendations
                    </IonText>
                    <IonIcon
                      color='light'
                      icon={
                        showRecommendations === index
                          ? chevronDownOutline
                          : chevronForwardOutline
                      }
                    />
                    <IonRippleEffect></IonRippleEffect>
                  </div>
                  {showRecommendations === index && (
                    <div className={classes.fuelingPlanDecDetails}>
                      <SanatizedHtml dirtyHtml={fuelingPlan.recommendations} />
                    </div>
                  )}
                  <div className={classes.fuelPlanDecButtonDivider}></div>
                </div>
              </div>
            </div>
            <div className={classes.fuelingPlanStatesDetails__pdfDoc}>
              <IonButton
                color='secondary'
                expand='full'
                onClick={async () => {
                  if (fuelingPlan.fueling_plan_doc) {
                    const response = await getPrivateFile(
                      fuelingPlan.fueling_plan_doc,
                      fuelingPlan.name
                    );
                  }
                }}
              >
                <div className={classes.fuelingPlanStatesDetails__pdfDocButton}>
                  <IonText className='big'>Download Fueling Plan</IonText>
                  <IonIcon icon={downloadOutline} />
                </div>
              </IonButton>
            </div>
          </IonSlide>
        ))}
      </IonSlides>
    );
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot='start'>
            <IonMenuButton></IonMenuButton>
          </IonButtons>
          <IonTitle>Fueling Plans</IonTitle>
          <IonButtons slot='end'>
            <IonButton id='open-date-picker-fueling'>
              <IonIcon icon={calendarOutline} />
            </IonButton>
          </IonButtons>
          <IonModal
            ref={datePicker}
            trigger='open-date-picker-fueling' 
          >
            <IonHeader>
              <IonToolbar>
                <IonTitle>Select Date</IonTitle>
                <IonButtons slot='end'>
                  <IonButton onClick={() => datePicker.current?.dismiss()}>
                    Close
                  </IonButton>
                </IonButtons>
              </IonToolbar>
            </IonHeader>
            <IonContent>
              <IonDatetime
                presentation='date'
                value={date}
                size='cover'
                onIonChange={(e) => {
                  if (e.detail.value) {
                    setDate(e.detail.value);
                  }
                }}
              ></IonDatetime>
            </IonContent>
          </IonModal>
        </IonToolbar>
      </IonHeader>
      <IonContent className={classes.ionContent}>
        <IonRefresher
          onIonRefresh={async (e) => {
            setIsRefreshing(true);
            await apolloClient.clearStore();
            await fetchFuelingPlans();
            e.detail.complete();
            setIsRefreshing(false);
          }}
          slot='fixed'
        >
          <IonRefresherContent></IonRefresherContent>
        </IonRefresher>
        {_renderContent()}
      </IonContent>
    </IonPage>
  );
};

export default observer(FuelingPlanPage);
